import React from "react";
import Card from "components/Card/Card";
import { useForm, SubmitHandler } from "react-hook-form";
import Input from "components/Input/Input";
import Button from "components/Button/Regular";
import Select from "components/Input/Select";
import Page from "components/Page/Page";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useActiveOrg } from "context/ActiveOrgContext";
import { CONTACT_TYPE, createOrgContact } from "services/contacts.service";
import vgToast from "utils/toast";

type FormValues = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
};

const titleToContactTitle = (t: string) => {
  const m: Record<string, string> = { mr: "MR", mrs: "MRS", ms: "MS", dr: "DR" };
  return m[t?.toLowerCase?.()] ?? t ?? "";
};

const NewCustomer = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { orgId } = useActiveOrg();
  const [submitting, setSubmitting] = React.useState(false);

  const {
    register,
    handleSubmit,
  } = useForm<FormValues>();

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (orgId <= 0) {
      vgToast.error("Select an active provider before adding a family.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const { data: res, error } = await createOrgContact(token, {
        orgId,
        contactFirstName: data.firstName,
        contactLastName: data.lastName,
        emailAddress: data.email,
        contactTitle: titleToContactTitle(data.title),
        contactType: CONTACT_TYPE.CUSTOMER,
      });
      if (error) {
        vgToast.error(error.message);
        return;
      }
      const newId =
        (res as { newRecordID?: number; NewRecordID?: number })?.newRecordID ??
        (res as { newRecordID?: number; NewRecordID?: number })?.NewRecordID;
      if (newId != null && Number.isFinite(Number(newId))) {
        navigate(`/app/clients/${newId}`);
      } else {
        navigate("/app/clients");
      }
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Card
        title="Add New Customer"
        subTitle="Fill the Following Details"
        className="mt-6"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="p-10">
          <div className="flex flex-col gap-6 mb-10 sm:flex-row sm:gap-x-10">
            <Select
              {...register("title")}
              label="Title"
              placeholder="Enter"
              className="w-24"
              options={[
                { title: "MR", value: "mr" },
                { title: "MRS", value: "mrs" },
              ]}
            />

            <Input
              {...register("firstName")}
              name="firstName"
              label="First Name"
              placeholder="Enter first name"
            />

            <Input
              {...register("lastName")}
              name="lastName"
              label="Last Name"
              placeholder="Enter last name"
            />
          </div>

          <Input
            {...register("email")}
            name="email"
            label="Email Address"
            placeholder="Enter email address"
            className="w-full mb-10 sm:w-1/2"
          />

          <Button color="Primary" type="submit" disabled={submitting}>
            <span>{submitting ? "Creating…" : "Create Client"}</span>
          </Button>
        </form>
      </Card>
    </Page>
  );
};

export default NewCustomer;
